import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { Tooltip } from 'modules/ui/atoms/Tooltip'
import { UiIcon } from '../../../ui/atoms/Icon'
import IconContainer from '../../../ui/atoms/Layout/IconContainer'
import styled from '@emotion/styled'
import { Checkbox, CheckboxProps } from 'modules/ui/atoms/Checkbox'
import { DEFAULT_THEME_COLORS, DEFAULT_BORDER_STYLES, getBodyFonts } from 'css/theme'

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
`

const CheckboxText = styled.span`
  text-align: left;
  ${getBodyFonts()}
  font-size: 14px;
`

const DCFMessage = styled.div`
  text-align: left;
  ${getBodyFonts()}
  font-size: 14px;
`

const DCFContainer = styled.div<{ isChecked: boolean }>`
  display: flex;
  flex-direction: column;
  width: auto;
  min-height: 85px;
  padding: 16px;
  border-radius: ${DEFAULT_BORDER_STYLES['--theme--border-radius-lg']};
  border: 1px solid;
  background-color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-95']};
  border-color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-95']};
`

const DCFTooltip = () => {
  const { t } = useTranslation()

  return (
    <Tooltip
      placement="top-start"
      text={t(
        'orderDonorCoveredFees.tooltip.text',
        'Transaction fees include both processing and donation software charges.',
      )}
      textAlign="left"
    >
      <UiIcon name="info" color="#78726E" />
    </Tooltip>
  )
}

const DCFComponent = () => {
  const { t, i18n } = useTranslation()
  const config = useConfig()
  const order = useOrder()

  const [isChecked, setIsChecked] = useState(config.dcf.checkedByDefault)

  const handleOnChange: CheckboxProps['onChange'] = () => {
    order.dcf.setValue({ isChecked: !isChecked })
    setIsChecked(!isChecked)
  }

  const checkboxMessage = t(
    'orderDonorCoveredFees.coverFees.labelWithFee',
    'Cover {{fee}} in transaction fees',
    {
      fee: new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: order.currency.value,
      }).format(order.dcf.value.fee),
    },
  )

  const appeal = config.dcf.dcfAppealMessage

  const defaultAppeal = t(
    'orderDonorCoveredFees.coverFees.uncheckedText',
    'This allows 100% of your donation to go towards our mission',
  )

  const dcfMessage = isChecked
    ? t('orderDonorCoveredFees.coverFees.checkedText', 'Thank you for covering the fees!')
    : appeal ?? defaultAppeal

  return (
    <DCFContainer isChecked={isChecked}>
      <StyledDiv>
        <CheckboxRow>
          <Checkbox
            centered={true}
            dataId="toggle-dcf"
            checked={isChecked}
            onChange={handleOnChange}
          >
            <CheckboxText>{checkboxMessage}</CheckboxText>
          </Checkbox>
          <IconContainer top="2px">
            <DCFTooltip />
          </IconContainer>
        </CheckboxRow>
        <DCFMessage>{dcfMessage}</DCFMessage>
      </StyledDiv>
    </DCFContainer>
  )
}

export default DCFComponent
